<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { getReplyPreviewFromMessage } from '@/modules/chat-matrix-fast/utils/replyPreview'
import {
  type ChatMentionCandidate,
  collectMentionUserIds,
  toMentionCandidate,
} from '@/modules/chat-matrix-fast/utils/mentions'
import {
  type InlineFormat,
  extractFirstUrl,
  wrapInlineFormat,
} from '@/modules/chat-matrix-fast/utils/messageRichText'
import { useChatMentions } from '@/modules/chat-matrix-fast/composables/useChatMentions'
import type { ChatMemberResponse } from '@/modules/chat-matrix-fast/services/chatApiService'
import MentionPicker from './chat-log/MentionPicker.vue'
import MessageLinkPreview from './chat-log/MessageLinkPreview.vue'
import ReplyPreviewThumbnail from './chat-log/ReplyPreviewThumbnail.vue'

interface IEditingMessage {
  id: number | string
  message: unknown
  originalText: string
}

interface IReplyTo {
  id: number | string
  message: {
    content?: string
    message?: string
    attachments?: Array<{ file: string; mime_type?: string; name?: string; thumbnail?: string }>
    [key: string]: unknown
  }
  senderId: number | string
  senderName: string
}

const props = defineProps<{
  modelValue: string
  editingMessage: IEditingMessage | null
  replyTo: IReplyTo | null
  members?: ChatMemberResponse[]
  excludeMentionKey?: string | null
  /** Lazy-load участников при первом открытии @-пикера */
  ensureMembers?: () => void | Promise<void>
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'send', mentionUserIds?: string[]): void
  (e: 'cancel-edit'): void
  (e: 'cancel-reply'): void
  (e: 'keydown', event: KeyboardEvent): void
  (e: 'paste-files', files: File[]): void
}>()

const textareaField = ref<{ $el?: HTMLElement } | null>(null)
const textRef = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
})

const candidates = computed(() =>
  (props.members ?? [])
    .map(toMentionCandidate)
    .filter((item): item is ChatMentionCandidate => Boolean(item)),
)

const pickerStyle = ref<Record<string, string>>({})
const composerLinkUrl = ref<string | null>(null)
const savedSelection = ref<{ start: number; end: number }>({ start: 0, end: 0 })
const hasTextSelection = computed(() => savedSelection.value.start !== savedSelection.value.end)

const replyPreview = computed(() => {
  if (!props.replyTo) {
    return null
  }

  const message = props.replyTo.message

  return getReplyPreviewFromMessage({
    content: message.content,
    message: message.message,
    attachments: message.attachments,
  })
})

const formatButtons: Array<{ format: InlineFormat; icon: string; label: string; hint: string }> = [
  { format: 'bold', icon: 'ri-bold', label: 'Жирный', hint: 'Ctrl+B' },
  { format: 'italic', icon: 'ri-italic', label: 'Курсив', hint: 'Ctrl+I' },
  { format: 'underline', icon: 'ri-underline', label: 'Подчёркнутый', hint: 'Ctrl+U' },
  { format: 'strike', icon: 'ri-strikethrough', label: 'Зачёркнутый', hint: 'Ctrl+Shift+X' },
  { format: 'code', icon: 'ri-code-line', label: 'Код', hint: 'Ctrl+E' },
]

let nativeTextarea: HTMLTextAreaElement | null = null

const getNativeTextarea = (): HTMLTextAreaElement | null => {
  if (nativeTextarea?.isConnected) {
    return nativeTextarea
  }
  const root = textareaField.value?.$el
  if (!root) return null
  nativeTextarea = root.querySelector('textarea')
  return nativeTextarea
}

const getCursor = () => {
  const el = getNativeTextarea()
  if (el && typeof el.selectionStart === 'number') {
    return el.selectionStart
  }
  return props.modelValue.length
}

const rememberSelection = () => {
  const el = getNativeTextarea()
  if (!el) return
  savedSelection.value = {
    start: el.selectionStart ?? 0,
    end: el.selectionEnd ?? 0,
  }
}

const updatePickerPosition = () => {
  const el = getNativeTextarea()
  if (!el) return

  const rect = el.getBoundingClientRect()
  const width = Math.max(rect.width, 240)
  const left = Math.min(rect.left, window.innerWidth - width - 8)

  pickerStyle.value = {
    position: 'fixed',
    left: `${Math.max(8, left)}px`,
    width: `${width}px`,
    bottom: `${Math.max(8, window.innerHeight - rect.top + 6)}px`,
    zIndex: '100000000',
  }
}

const setTextAndCursor = (value: string, cursor: number, selectEnd?: number) => {
  emit('update:modelValue', value)
  nextTick(() => {
    const el = getNativeTextarea()
    if (!el) return
    el.focus()
    const end = selectEnd ?? cursor
    el.setSelectionRange(cursor, end)
    savedSelection.value = { start: cursor, end }
    updatePickerPosition()
  })
}

const applyFormat = (format: InlineFormat) => {
  const el = getNativeTextarea()
  let from = savedSelection.value.start
  let to = savedSelection.value.end

  if (el && el.selectionStart !== el.selectionEnd) {
    from = el.selectionStart
    to = el.selectionEnd
    savedSelection.value = { start: from, end: to }
  }

  const next = wrapInlineFormat(props.modelValue, from, to, format)
  setTextAndCursor(next.text, next.selectStart, next.selectEnd)
}

const syncComposerLinkPreview = (text: string) => {
  composerLinkUrl.value = extractFirstUrl(text)
}

const {
  isOpen: isMentionOpen,
  filtered: mentionItems,
  highlightedIndex: mentionHighlight,
  syncFromCursor: syncMentionFromCursor,
  select: selectMention,
  close: closeMentions,
  onKeydown: onMentionKeydown,
} = useChatMentions({
  text: textRef,
  candidates,
  excludeKey: toRef(props, 'excludeMentionKey'),
  getCursor,
  setTextAndCursor,
})

const setMentionHighlight = (index: number) => {
  mentionHighlight.value = index
}

const refreshMentions = (textOverride?: string) => {
  const textarea = getNativeTextarea()
  if (!textarea || document.activeElement !== textarea) {
    closeMentions()
    return
  }
  syncMentionFromCursor(textOverride)
  if (isMentionOpen.value) {
    nextTick(() => updatePickerPosition())
  }
}

/** Скрыть пикер при уходе фокуса (диалоги, сайдбары) — select идёт через mousedown.prevent. */
const onTextareaBlur = () => {
  nextTick(() => {
    const textarea = getNativeTextarea()
    if (textarea && document.activeElement === textarea) return
    closeMentions()
  })
}

watch(
  () => props.modelValue,
  value => {
    const len = value.length
    const { start, end } = savedSelection.value
    if (start > len || end > len) {
      savedSelection.value = {
        start: Math.min(start, len),
        end: Math.min(end, len),
      }
    }
    syncComposerLinkPreview(value)
    nextTick(() => refreshMentions(value))
  },
  { immediate: true },
)

watch(isMentionOpen, open => {
  if (open) {
    void props.ensureMembers?.()
    nextTick(() => updatePickerPosition())
  }
})

const onInput = (value: unknown) => {
  const next = String(value ?? '')
  emit('update:modelValue', next)
  syncComposerLinkPreview(next)
  nextTick(() => {
    refreshMentions(next)
    rememberSelection()
  })
}

const onPaste = (event: ClipboardEvent) => {
  const items = event.clipboardData?.items
  if (items?.length) {
    const imageFiles: File[] = []
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }
    if (imageFiles.length) {
      event.preventDefault()
      emit('paste-files', imageFiles)
      return
    }
  }
  nextTick(() => syncComposerLinkPreview(props.modelValue))
}

const onKeydown = (event: KeyboardEvent) => {
  if (onMentionKeydown(event)) return
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    onSend()
    return
  }
  if ((event.metaKey || event.ctrlKey) && !event.altKey) {
    const key = event.key.toLowerCase()
    let format: InlineFormat | null = null
    if (key === 'b') format = 'bold'
    else if (key === 'i') format = 'italic'
    else if (key === 'u') format = 'underline'
    else if (key === 'e') format = 'code'
    else if (key === 'x' && event.shiftKey) format = 'strike'

    if (format) {
      event.preventDefault()
      applyFormat(format)
      return
    }
  }
  if (
    event.shiftKey
    && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
  ) {
    nextTick(() => rememberSelection())
  }
  emit('keydown', event)
}

const onSend = () => {
  const mentionUserIds = collectMentionUserIds(props.modelValue, candidates.value)
  emit('send', mentionUserIds.length ? mentionUserIds : undefined)
}

const onNativeMouseUp = () => {
  requestAnimationFrame(() => rememberSelection())
}
const onNativeKeyUp = () => rememberSelection()
const onNativeSelect = () => rememberSelection()

const bindNativeTextarea = () => {
  const el = getNativeTextarea()
  if (!el || el.dataset.formatBound === '1') return
  el.dataset.formatBound = '1'
  el.addEventListener('select', onNativeSelect)
  el.addEventListener('mouseup', onNativeMouseUp)
  el.addEventListener('keyup', onNativeKeyUp)
}

const unbindNativeTextarea = () => {
  const el = nativeTextarea
  if (!el) return
  el.removeEventListener('select', onNativeSelect)
  el.removeEventListener('mouseup', onNativeMouseUp)
  el.removeEventListener('keyup', onNativeKeyUp)
  delete el.dataset.formatBound
}

const onViewportChange = () => {
  if (isMentionOpen.value) updatePickerPosition()
}

onMounted(() => {
  nextTick(() => bindNativeTextarea())
})

window.addEventListener('resize', onViewportChange)
window.addEventListener('scroll', onViewportChange, true)

onBeforeUnmount(() => {
  unbindNativeTextarea()
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})

defineExpose({
  focus: () => {
    getNativeTextarea()?.focus()
  },
  getNativeTextarea,
})
</script>

<template>
  <VForm
    class="chat-log-message-form mb-5 mx-5"
    @submit.prevent="onSend"
  >
    <div
      v-if="editingMessage"
      class="edit-context-in-input d-flex align-center gap-2 py-2 px-3 rounded mb-2"
    >
      <VIcon icon="ri-edit-line" size="20" color="primary" />
      <span class="text-primary font-weight-medium">Редактирование</span>
      <span class="edit-context-original-text text-medium-emphasis flex-grow-1 text-truncate">
        {{ editingMessage.originalText }}
      </span>
      <IconBtn size="small" @click="emit('cancel-edit')">
        <VIcon icon="ri-close-line" size="18" />
      </IconBtn>
    </div>
    <div v-if="replyTo" class="reply-context mb-2">
      <div class="reply-context-inner">
        <div class="reply-context-line" />
        <ReplyPreviewThumbnail
          v-if="replyPreview?.thumbnail"
          :src="replyPreview.thumbnail"
        />
        <div class="reply-context-content">
          <div class="reply-context-sender">
            В ответ {{ replyTo.senderName }}
          </div>
          <div class="reply-context-text">
            {{ replyPreview?.text }}
          </div>
        </div>
        <IconBtn size="small" @click="emit('cancel-reply')">
          <VIcon icon="ri-close-line" size="16" />
        </IconBtn>
      </div>
    </div>

    <MessageLinkPreview
      v-if="composerLinkUrl"
      :url="composerLinkUrl"
      class="mb-2"
    />

    <div class="chat-composer">
      <div
        v-if="hasTextSelection"
        class="chat-format-toolbar"
      >
        <button
          v-for="btn in formatButtons"
          :key="btn.format"
          type="button"
          class="chat-format-btn"
          :title="`${btn.label} (${btn.hint})`"
          :aria-label="btn.label"
          @mousedown.prevent
          @click="applyFormat(btn.format)"
        >
          <VIcon
            :icon="btn.icon"
            size="18"
          />
        </button>
      </div>

      <div class="chat-input-field-wrap">
        <Teleport to="body">
          <MentionPicker
            v-if="isMentionOpen"
            :items="mentionItems"
            :highlighted-index="mentionHighlight"
            :panel-style="pickerStyle"
            @select="selectMention"
            @highlight="setMentionHighlight"
          />
        </Teleport>

        <VTextarea
          ref="textareaField"
          :model-value="modelValue"
          variant="solo"
          density="default"
          class="chat-message-input"
          placeholder="Сообщение ..."
          autofocus
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="true"
          name="chat-message"
          rows="1"
          auto-grow
          max-rows="6"
          @update:model-value="onInput"
          @keydown="onKeydown"
          @paste="onPaste"
          @blur="onTextareaBlur"
          @click="() => { bindNativeTextarea(); refreshMentions(); rememberSelection() }"
          @focus="() => { bindNativeTextarea(); refreshMentions() }"
        >
          <template #append-inner>
            <div class="d-flex gap-1 align-center">
              <slot name="actions" />
              <div class="d-none d-md-block">
                <VBtn
                  :append-icon="
                    editingMessage ? 'ri-check-line' : 'ri-send-plane-line'
                  "
                  class="chat-send-btn"
                  @click="onSend"
                >
                  {{ editingMessage ? "Сохранить" : "Отправить" }}
                </VBtn>
              </div>
              <IconBtn
                class="d-block d-md-none chat-send-icon-btn"
                :title="editingMessage ? 'Сохранить' : 'Отправить'"
                @click="onSend"
              >
                <VIcon :icon="editingMessage ? 'ri-check-line' : 'ri-send-plane-line'" />
              </IconBtn>
            </div>
          </template>
        </VTextarea>
      </div>
    </div>
  </VForm>
</template>

<style lang="scss" scoped>
.chat-log-message-form {
  flex-shrink: 0;
  background-color: rgb(var(--v-theme-background));

  .chat-message-input {
    :deep(.v-field__append-inner) {
      align-items: end;
      padding-block: 8px;
    }
  }
}

.chat-composer {
  border: 1px solid rgba(var(--v-theme-outline), 0.16);
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.chat-format-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(var(--v-theme-outline), 0.1);
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.chat-format-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.75);
  cursor: pointer;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.08);
    color: rgba(var(--v-theme-on-surface), 0.95);
  }
}

.chat-input-field-wrap {
  position: relative;

  :deep(.v-field) {
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  :deep(.v-field__outline) {
    display: none;
  }
}

.edit-context-in-input {
  background: rgba(var(--v-theme-surface), 0.8);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
}

.reply-context {
  padding: 8px 12px;
  background: rgba(var(--v-theme-surface), 0.8);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
}

.reply-context-inner {
  display: flex;
  gap: 8px;
  align-items: center;
}

.reply-context-line {
  width: 3px;
  background: rgb(var(--v-theme-primary));
  border-radius: 2px;
  flex-shrink: 0;
}

.reply-context-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.reply-context-sender {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reply-context-text {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}
</style>
